import { useState, useEffect, useRef, useCallback } from "react";
import {
  canCloseDropdownOnMouseLeave,
  eventOccurredInside,
} from "../utils/dropdownMenuEvents";

const HOVER_CLOSE_DELAY_MS = 180;
const SCROLL_CLOSE_GRACE_MS = 400;

const useDropdownMenu = () => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const scrollCloseArmedRef = useRef(false);
  const armScrollCloseTimerRef = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const clearArmScrollTimer = useCallback(() => {
    if (armScrollCloseTimerRef.current !== null) {
      window.clearTimeout(armScrollCloseTimerRef.current);
      armScrollCloseTimerRef.current = null;
    }
  }, []);

  const disarmScrollClose = useCallback(() => {
    clearArmScrollTimer();
    scrollCloseArmedRef.current = false;
  }, [clearArmScrollTimer]);

  const armScrollCloseAfterOpen = useCallback(() => {
    disarmScrollClose();
    armScrollCloseTimerRef.current = window.setTimeout(() => {
      scrollCloseArmedRef.current = true;
      armScrollCloseTimerRef.current = null;
    }, SCROLL_CLOSE_GRACE_MS);
  }, [disarmScrollClose]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setMenuAbierto(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    disarmScrollClose();
    setMenuAbierto(false);
  }, [clearCloseTimer, disarmScrollClose]);

  const toggleMenu = () => {
    clearCloseTimer();
    setMenuAbierto((prev) => {
      if (prev) {
        disarmScrollClose();
        return false;
      }
      armScrollCloseAfterOpen();
      return true;
    });
  };

  const handleMouseEnter = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handleMouseLeave = useCallback(() => {
    if (menuAbierto && canCloseDropdownOnMouseLeave()) {
      scheduleClose();
    }
  }, [menuAbierto, scheduleClose]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !eventOccurredInside(event, menuRef.current)) {
        setMenuAbierto(false);
        disarmScrollClose();
      }
    };

    const handleScroll = (event) => {
      if (eventOccurredInside(event, menuRef.current)) {
        return;
      }
      if (!scrollCloseArmedRef.current) {
        return;
      }
      setMenuAbierto(false);
      disarmScrollClose();
    };

    const handleResize = () => {
      setMenuAbierto(false);
      disarmScrollClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      clearCloseTimer();
      clearArmScrollTimer();
    };
  }, [clearArmScrollTimer, clearCloseTimer, disarmScrollClose]);

  return {
    menuAbierto,
    toggleMenu,
    closeMenu,
    menuRef,
    handleMouseEnter,
    handleMouseLeave,
  };
};

export default useDropdownMenu;
